import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtPKR } from "@/lib/format";

const Overlay = ({ children, testid }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(4,14,36,0.82)] backdrop-blur-sm rounded-[inherit] p-4" data-testid={testid}>
    <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} className="w-full max-w-md text-center rounded-2xl border border-[rgba(232,199,102,0.6)] bg-gradient-to-b from-[#0F2D66] to-[#061533] p-6 shadow-[0_0_60px_-10px_rgba(201,162,39,0.6)]">
      {children}
    </motion.div>
  </motion.div>
);

export const DndOverlays = ({ game, busy, onDecision, onFinal, onAgain }) => (
  <AnimatePresence>
    {game?.status === "offer" && game.current_offer && (
      <Overlay testid="dnd-offer">
        <Phone className="w-10 h-10 text-[#E8C766] mx-auto animate-pulse" />
        <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mt-3">The Banker is calling</p>
        <p className="font-head text-4xl sm:text-5xl font-extrabold text-gradient mt-2 keep-ltr" data-testid="dnd-offer-amount">{fmtPKR(game.current_offer.amount)}</p>
        <p className="text-xs text-gray-400 mt-1">{game.current_offer.percent}% of your {fmtPKR(game.amount)} after {game.current_offer.after} boxes</p>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <Button disabled={busy} onClick={() => onDecision(true)} className="h-14 bg-[#C9A227] hover:bg-[#D9B44A] text-[#061533] font-head font-extrabold text-lg" data-testid="dnd-deal-button">DEAL</Button>
          <Button disabled={busy} onClick={() => onDecision(false)} className="h-14 bg-red-600 hover:bg-red-500 text-pure font-head font-extrabold text-lg" data-testid="dnd-nodeal-button">NO DEAL</Button>
        </div>
      </Overlay>
    )}
    {game?.status === "final_swap" && (
      <Overlay testid="dnd-final">
        <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Final decision</p>
        <p className="font-head text-2xl font-extrabold text-white mt-2">Keep box #{game.own_box} or swap?</p>
        <p className="text-xs text-gray-400 mt-2 keep-ltr">Two prizes left: {(game.remaining_prizes || []).map(fmtPKR).join(" and ")}</p>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <Button disabled={busy} onClick={() => onFinal(false)} className="h-12 bg-[#C9A227] hover:bg-[#D9B44A] text-[#061533] font-bold" data-testid="dnd-keep-button">KEEP MY BOX</Button>
          <Button disabled={busy} onClick={() => onFinal(true)} className="h-12 bg-[#1F4690] hover:bg-[#163A7D] text-pure font-bold border border-[rgba(232,199,102,0.5)]" data-testid="dnd-swap-button">SWAP</Button>
        </div>
      </Overlay>
    )}
    {game?.status === "completed" && (
      <Overlay testid="dnd-result">
        <Trophy className="w-10 h-10 text-[#E8C766] mx-auto" />
        <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mt-3">{game.decision === "deal" ? "Deal accepted" : game.decision === "swap" ? "You swapped" : "You kept your box"}</p>
        <p className="font-head text-4xl sm:text-5xl font-extrabold text-gradient mt-2 keep-ltr" data-testid="dnd-final-prize">{fmtPKR(game.payout)}</p>
        {game.bonus > 0 && <p className="text-xs text-gray-400">includes {fmtPKR(game.bonus)} bonus</p>}
        <p className="text-xs text-gray-400 mt-1">Your box #{game.own_box} held {fmtPKR(game.boxes[game.own_box - 1].prize)}</p>
        <Button onClick={onAgain} className="mt-6 bg-[#C9A227] hover:bg-[#D9B44A] text-[#061533] font-bold" data-testid="dnd-play-again">Play Again</Button>
      </Overlay>
    )}
  </AnimatePresence>
);
